import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { brand } from '../../../theme/colors';
import { Episode, fetchSeasonEpisodes } from '../tmdb';
import {
  SeasonSummary,
  TVShow,
  currentSeason,
  newEpisodesBadge,
  progress,
  seasonWatched,
} from '../types';

interface EpisodesDialogProps {
  show: TVShow | null;
  isReadOnly: boolean;
  onClose: () => void;
  // Replaces the ticked episodes for each season given.
  onSetTicks: (show: TVShow, ticks: Record<number, number[]>) => void;
}

const range = (count: number) => Array.from({ length: count }, (_, i) => i + 1);

const EpisodesDialog = ({
  show,
  isReadOnly,
  onClose,
  onSetTicks,
}: EpisodesDialogProps) => {


  const [episodes, setEpisodes] = useState<Record<number, Episode[]>>({});
  const [loadError, setLoadError] = useState(false);
  const [expanded, setExpanded] = useState<number | false>(false);

  const tmdbId = show?.tmdbId;
  const showId = show?.id;

  // Load every season's episode names when the dialog opens, so season and
  // whole-show ticks use TMDB's real episode numbers.
  useEffect(() => {
    if (!show) return;
    setEpisodes({});
    setLoadError(false);
    setExpanded(currentSeason(show)?.n ?? false);

    let cancelled = false;
    show.tmdb.seasons
      .filter((season) => season.episodeCount > 0)
      .forEach((season) => {
        fetchSeasonEpisodes(show.tmdbId, season.n)
          .then((list) => {
            if (!cancelled) setEpisodes((prev) => ({ ...prev, [season.n]: list }));
          })
          .catch(() => {
            if (!cancelled) setLoadError(true);
          });
      });

    return () => {
      cancelled = true;
    };
    // Reload only when a different show is opened, not on every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showId, tmdbId]);

  if (!show) return null;

  // Aired episodes come first in TMDB's list; the summary says how many.
  const airedNumbers = (season: SeasonSummary) => {
    const list = episodes[season.n];
    return list
      ? list.slice(0, season.aired).map((e) => e.number)
      : range(season.aired);
  };

  const ticksFor = (season: SeasonSummary) => show.watched[season.n] ?? [];

  const toggleEpisode = (season: SeasonSummary, number: number) => {
    const ticks = ticksFor(season);
    const next = ticks.includes(number)
      ? ticks.filter((n) => n !== number)
      : [...ticks, number].sort((a, b) => a - b);
    onSetTicks(show, { [season.n]: next });
  };

  const toggleSeason = (season: SeasonSummary) => {
    const full = seasonWatched(show, season) >= season.aired;
    onSetTicks(show, { [season.n]: full ? [] : airedNumbers(season) });
  };

  const { seen, aired } = progress(show);
  const allSeen = aired > 0 && seen >= aired;

  const toggleWholeShow = () => {
    if (
      allSeen &&
      !window.confirm(`Untick every episode of ${show.title}?`)
    ) {
      return;
    }
    const ticks: Record<number, number[]> = {};
    show.tmdb.seasons.forEach((season) => {
      ticks[season.n] = allSeen ? [] : airedNumbers(season);
    });
    onSetTicks(show, ticks);
  };

  const badge = newEpisodesBadge(show);
  const latestAiredSeason = Math.max(
    0,
    ...show.tmdb.seasons.filter((s) => s.aired > 0).map((s) => s.n)
  );

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: 2,
          maxHeight: 'calc(100% - 32px)',
          width: 'calc(100% - 32px)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          component="img"
          src={show.image}
          alt=""
          sx={{
            width: 54,
            aspectRatio: '2 / 3',
            objectFit: 'cover',
            borderRadius: 1,
            flex: 'none',
            bgcolor: 'action.hover',
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" sx={{ lineHeight: 1.1 }}>
            {show.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {[show.year || null, show.tmdb.status || null].filter(Boolean).join(' · ')}
            {aired > 0 && ` · ${seen} of ${aired} aired episodes seen`}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ ml: 'auto', alignSelf: 'flex-start' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 1 }}>
        {loadError && (
          <Alert severity="warning" sx={{ my: 1 }}>
            Couldn't load some episode names from TMDB.
          </Alert>
        )}

        {aired > 0 && (
          <Box
            component="label"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              cursor: isReadOnly ? 'default' : 'pointer',
            }}
          >
            <Checkbox
              checked={allSeen}
              indeterminate={seen > 0 && !allSeen}
              disabled={isReadOnly}
              onChange={toggleWholeShow}
            />
            <Typography sx={{ fontWeight: 800 }}>Whole show</Typography>
            <Typography variant="body2" color="text.secondary">
              {seen}/{aired}
            </Typography>
          </Box>
        )}

        {show.tmdb.seasons.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            TMDB has no seasons listed for this show yet.
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1.5 }}>
          {show.tmdb.seasons.map((season) => {
            const watchedCount = seasonWatched(show, season);
            const notAired = season.aired === 0;
            const full = !notAired && watchedCount >= season.aired;
            const list = episodes[season.n];
            const ticks = ticksFor(season);

            return (
              <Accordion
                key={season.n}
                disableGutters
                expanded={expanded === season.n}
                onChange={(_, isOpen) => setExpanded(isOpen ? season.n : false)}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: '10px !important',
                  overflow: 'hidden',
                  backgroundImage: 'none',
                  '&::before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: 'action.hover',
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      gap: 1,
                      my: 0.5,
                    },
                  }}
                >
                  <Checkbox
                    checked={full}
                    indeterminate={watchedCount > 0 && !full}
                    disabled={isReadOnly || notAired}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    onChange={() => toggleSeason(season)}
                    sx={{ p: 0.5 }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: notAired ? 'text.disabled' : 'text.primary',
                    }}
                  >
                    {season.name || `Season ${season.n}`}
                  </Typography>
                  {badge && season.n === latestAiredSeason && (
                    <Box
                      component="span"
                      sx={{
                        px: 0.75,
                        borderRadius: 999,
                        bgcolor: brand.pink,
                        color: brand.white,
                        fontSize: 10,
                        fontWeight: 800,
                      }}
                    >
                      NEW
                    </Box>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {notAired ? 'Not aired yet' : `${watchedCount}/${season.aired}`}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  {season.episodeCount === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ px: 2, py: 1.5 }}
                    >
                      No episodes announced yet.
                    </Typography>
                  ) : !list ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : (
                    list.map((episode, index) => {
                      const hasAired = index < season.aired;
                      return (
                        <Box
                          key={episode.number}
                          component="label"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pl: 1.5,
                            pr: 2,
                            py: 0.25,
                            borderTop: 1,
                            borderColor: 'divider',
                            color: hasAired ? 'text.primary' : 'text.disabled',
                            cursor: hasAired && !isReadOnly ? 'pointer' : 'default',
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={ticks.includes(episode.number)}
                            disabled={isReadOnly || !hasAired}
                            onChange={() => toggleEpisode(season, episode.number)}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ width: 32, flex: 'none' }}
                          >
                            E{episode.number}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {episode.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 'auto', pl: 1, whiteSpace: 'nowrap' }}
                          >
                            {episode.airDate
                              ? `${hasAired ? '' : 'Airs '}${new Date(
                                  `${episode.airDate}T00:00:00`
                                ).toLocaleDateString(undefined, {
                                  day: 'numeric',
                                  month: 'short',
                                  year:
                                    episode.airDate.slice(0, 4) ===
                                    String(new Date().getFullYear())
                                      ? undefined
                                      : 'numeric',
                                })}`
                              : ''}
                          </Typography>
                        </Box>
                      );
                    })
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ pb: 'max(env(safe-area-inset-bottom), 8px)', pr: 2 }}>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EpisodesDialog;
